
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Storage, ref, uploadBytesResumable, UploadTask, getDownloadURL, deleteObject } from '@angular/fire/storage'

import { MediaFile, MediaFileType } from '@motes-sdk/models';

// OBS: This import is needed, otherwise webpack will (for some reason) delete
// the storage reference of angular fire2 when building to prod with AOT
// and we will get the evil '.storage is not a function' error

export enum UploadStatus {
    IDLE = 'idle',
    CROPPING = 'cropping',
    RUNNING = 'running',
    ERROR = 'error',
    DONE = 'done'
}

export interface UploadState {
    status: UploadStatus,
    percentage: number,
    downloadUrl?: string,
    storageRef?: string,
    bytesTransfered?: number,
    bytesTotal?: number,
    metaData?: any,
    error?: Error;
}

@Injectable({ providedIn: 'root' })
export class UploadService {

    private readonly storage: Storage = inject(Storage);

    constructor() {
    }

    public uploadFile(file: File, storagePath: string, metaData = null): Observable<UploadState> {
        if(!file) console.log(new Error('no file').stack);
        return new Observable((obs) => {
            // Create task and state obj
            const storageRef = ref(this.storage, storagePath);
            var task: UploadTask = uploadBytesResumable(storageRef, file,  metaData);
            var state: UploadState = {
                percentage: 0,
                status: UploadStatus.RUNNING,
                bytesTransfered: 0,
                bytesTotal: 0
            };

            // When task is done --> run next with url and complete on obs
            task.then((snapshot) => {
                getDownloadURL(snapshot.ref).then((url) => {
                    state.percentage = 100;
                    state.status = UploadStatus.DONE;
                    state.downloadUrl = url;
                    obs.next(state);
                    obs.complete();
                });
            });

            // On error --> run next with error and file error on obs
            task.catch((err: Error) => {
                state.status = UploadStatus.ERROR;
                state.error = err;
                // obs.next(state);
                obs.error(err);
            });

            // When snapshot changes --> update state and run next on obs
            task.on('state_changed', (snap) => {
                state.bytesTotal = snap.totalBytes;
                state.bytesTransfered = snap.bytesTransferred;
                state.metaData = snap.metadata;
                state.percentage = (state.bytesTransfered / state.bytesTotal) * 100;
                state.storageRef = snap.ref.fullPath;
                obs.next(state);
            });
        });
    }

    public async deleteFile(file: MediaFile): Promise<any> {
        if(file.url && !file.storage) {
            console.warn("Could not delete - storage path is empty");
            return null;
        }

        // Don't attempt to delete file if url is not used
        if(!file.storage || !file.url) return null;

        const fileStorageRef = ref(this.storage, file.storage);
        const deletePromise = deleteObject(fileStorageRef).catch(err => {
            console.log("File does not exist on firebase storage. Wont delete: ", file.storage);
            return null;
        });

        // If file is not a type that has fallback version, return here
        if(file.type != MediaFileType.HD &&
            file.type != MediaFileType.PREVIEW &&
            file.type != MediaFileType.SQUARE &&
            file.type != MediaFileType.THUMB
        ) {
            return deletePromise;
        }

        const fallbackStoragePath = file.storage.replace('webp', 'jpeg');
        const fallbackStorageRef = ref(this.storage, fallbackStoragePath);

        const deleteFallbackPromise = deleteObject(fallbackStorageRef).catch(err => {
            console.log("File does not exist on firebase storage. Wont delete: ", file.storage);
            return null;
        });

        return Promise.all([deletePromise, deleteFallbackPromise]);
    }
}
